import { Box, Button, TextField, Typography } from "@mui/material";
import { CreateDeviceUiState } from "@types";
import { useState } from "react";
import { WebsiteLoadingModal } from "src/components/DeviceCreation/modal-list-items/ImportDeviceModalComponents/WebsiteLoadingModal";
import { useParseWebsiteToDeviceMutation } from "src/hooks/mutations";

export const ImportDeviceModalContent = ({
  setDevice,
}: {
  setDevice: (device: CreateDeviceUiState) => void;
}) => {
  const [productUrl, setProductUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const { mutateAsync: parseWebsiteToDevice } =
    useParseWebsiteToDeviceMutation();

  const validateUrl = (url: string): boolean => {
    try {
      // Requires a domain name with a TLD (like .com) but doesn't require http/https or www
      const urlPattern =
        /^(https?:\/\/)?(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/;
      return urlPattern.test(url);
    } catch {
      return false;
    }
  };

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setProductUrl(url);
    setUrlError(null);
  };

  const handleGetProductData = async () => {
    if (!validateUrl(productUrl)) {
      setUrlError(
        "Please enter a valid URL, i.e https://www.mywebsite.com/my-cool-product"
      );
      return;
    }

    setIsLoading(true);

    const response = await parseWebsiteToDevice({
      url: productUrl,
    });

    if ("error" in response) {
      setUrlError(response.error);
      setIsLoading(false);
      return;
    }

    // Convert CreateDevice (from API) to CreateDeviceUiState (for modal UI)
    const device: CreateDeviceUiState = {
      ...response,
      isUK: response.isUK ?? false,
      isCanada: response.isCanada ?? false,
      components: response.components.map((component, componentIndex) => ({
        ...component,
        index: componentIndex,
        subComponents: component.subComponents?.map(
          (subComponent, subComponentIndex) => ({
            ...subComponent,
            index: subComponentIndex,
          })
        ),
      })),
    };

    setDevice(device);
    setIsLoading(false);
  };

  const handleCancel = () => {
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-y-4">
      <Typography variant="body1" className="w-3/4">
        Kickstart your certification by providing our AI a{" "}
        <strong>webpage with text descriptions</strong> of your product (like a
        product, FAQ, or landing page).
      </Typography>
      <TextField
        fullWidth
        label="Device URL"
        placeholder="www.mywebsite.com/my-cool-product"
        value={productUrl}
        onChange={handleUrlChange}
        error={!!urlError}
        helperText={urlError}
        required
        sx={{ mb: urlError ? -1.5 : 1 }}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!productUrl || !!urlError || isLoading}
          onClick={handleGetProductData}
        >
          Get Product Data
        </Button>
      </Box>
      <WebsiteLoadingModal
        isLoading={isLoading}
        websiteUrl={productUrl}
        onCancel={handleCancel}
      />
    </div>
  );
};
